/**
 * "Edit clinic information" (RL-1120): the staff branch shape from
 * `GET /branches` and the form <-> `PATCH /branches/:id` mapping (API RL-1080).
 * Nullable text fields clear with null; lists replace the stored list ([] clears);
 * the map pin is sent as a latitude/longitude pair.
 */
export interface BranchInfo {
  id: string;
  code: string;
  name: string;
  status: string;
  phone: string | null;
  email: string | null;
  address: {
    line1: string | null;
    line2: string | null;
    postcode: string | null;
    city: string | null;
    state: string | null;
  };
  mohRegistrationNo: string | null;
  whatsapp: string | null;
  latitude: number | null;
  longitude: number | null;
  parkingNote: string | null;
  panelsAccepted: string[];
  servicesOffered: string[];
  rooms: string[];
  facilities: string[];
}

export interface BranchForm {
  addressLine1: string;
  addressLine2: string;
  postcode: string;
  city: string;
  state: string;
  phone: string;
  email: string;
  whatsapp: string;
  latitude: string;
  longitude: string;
  parkingNote: string;
  panelsAccepted: string;
  servicesOffered: string;
  facilities: string;
  mohRegistrationNo: string;
}

type TextKey =
  | 'addressLine1'
  | 'addressLine2'
  | 'postcode'
  | 'city'
  | 'state'
  | 'phone'
  | 'email'
  | 'whatsapp'
  | 'parkingNote'
  | 'mohRegistrationNo';

/** Same limits as the API DTO, so most mistakes are caught before saving. */
export const LIST_LIMITS = { items: 30, length: 80 } as const;
const WHATSAPP = /^\+?[0-9][0-9 ()-]{5,30}$/;

export function toForm(b: BranchInfo): BranchForm {
  return {
    addressLine1: b.address.line1 ?? '',
    addressLine2: b.address.line2 ?? '',
    postcode: b.address.postcode ?? '',
    city: b.address.city ?? '',
    state: b.address.state ?? '',
    phone: b.phone ?? '',
    email: b.email ?? '',
    whatsapp: b.whatsapp ?? '',
    latitude: b.latitude === null ? '' : String(b.latitude),
    longitude: b.longitude === null ? '' : String(b.longitude),
    parkingNote: b.parkingNote ?? '',
    panelsAccepted: b.panelsAccepted.join('\n'),
    servicesOffered: b.servicesOffered.join('\n'),
    facilities: b.facilities.join(', '),
    mohRegistrationNo: b.mohRegistrationNo ?? '',
  };
}

const lines = (v: string) =>
  v
    .split('\n')
    .map((x) => x.trim())
    .filter(Boolean);
const commas = (v: string) =>
  v
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
const text = (v: string) => v.trim() || null;
const sameList = (a: string[], b: string[]) =>
  a.length === b.length && a.every((x, i) => x === b[i]);

function current(b: BranchInfo, key: TextKey): string | null {
  switch (key) {
    case 'addressLine1':
      return b.address.line1;
    case 'addressLine2':
      return b.address.line2;
    case 'postcode':
      return b.address.postcode;
    case 'city':
      return b.address.city;
    case 'state':
      return b.address.state;
    default:
      return b[key];
  }
}

function coordinate(
  raw: string,
  limit: number,
  label: string,
): { value: number | null; error?: string } {
  const v = raw.trim();
  if (!v) return { value: null };
  const n = Number(v);
  if (!Number.isFinite(n))
    return { value: null, error: `${label} must be a number` };
  if (n < -limit || n > limit)
    return {
      value: null,
      error: `${label} must be between -${limit} and ${limit}`,
    };
  return { value: n };
}

function listError(items: string[]): string | undefined {
  if (items.length > LIST_LIMITS.items)
    return `At most ${LIST_LIMITS.items} entries`;
  if (items.some((x) => x.length > LIST_LIMITS.length))
    return `Each entry is at most ${LIST_LIMITS.length} characters`;
  return undefined;
}

/**
 * Builds the PATCH body with only the fields that changed (the API audits the
 * field names sent), plus client-side errors keyed by API field name.
 */
export function buildPatch(
  b: BranchInfo,
  f: BranchForm,
): { body: Record<string, unknown>; errors: Record<string, string> } {
  const body: Record<string, unknown> = {};
  const errors: Record<string, string> = {};

  const textKeys: TextKey[] = [
    'addressLine1',
    'addressLine2',
    'postcode',
    'city',
    'state',
    'phone',
    'email',
    'whatsapp',
    'parkingNote',
    'mohRegistrationNo',
  ];
  for (const key of textKeys) {
    const next = text(f[key]);
    if (next !== current(b, key)) body[key] = next;
  }
  if (typeof body.whatsapp === 'string' && !WHATSAPP.test(body.whatsapp))
    errors.whatsapp = 'Use an international number, e.g. +60 12-345 6789';

  const lat = coordinate(f.latitude, 90, 'Latitude');
  const lng = coordinate(f.longitude, 180, 'Longitude');
  if (lat.error) errors.latitude = lat.error;
  if (lng.error) errors.longitude = lng.error;
  if (!lat.error && !lng.error) {
    if ((lat.value === null) !== (lng.value === null)) {
      errors[lat.value === null ? 'latitude' : 'longitude'] =
        'Enter latitude and longitude together, or clear both';
    } else if (lat.value !== b.latitude || lng.value !== b.longitude) {
      body.latitude = lat.value;
      body.longitude = lng.value;
    }
  }

  const lists = {
    panelsAccepted: [lines(f.panelsAccepted), b.panelsAccepted],
    servicesOffered: [lines(f.servicesOffered), b.servicesOffered],
    facilities: [commas(f.facilities), b.facilities],
  } as const;
  for (const [key, [next, was]] of Object.entries(lists)) {
    const error = listError(next);
    if (error) errors[key] = error;
    else if (!sameList(next, was)) body[key] = next;
  }
  return { body, errors };
}

/** API field errors keyed by top-level field ("panelsAccepted.0" -> "panelsAccepted"). */
export function fieldErrorMap(
  fieldErrors: { field: string; message: string }[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const e of fieldErrors) {
    const key = e.field.split('.')[0];
    out[key] ??= e.message;
  }
  return out;
}

/** "2.2, 102.25" for the card; null when no pin is set. */
export function pinLabel(b: BranchInfo): string | null {
  return b.latitude === null || b.longitude === null
    ? null
    : `${b.latitude}, ${b.longitude}`;
}
